/**
 * src/hooks/useMyBugUpdates.js
 *
 * The viewer's bug reports whose status has moved past 'reported' and that
 * they have not yet marked as seen at that status. One store per
 * (tenant, user), shared by the ◎ badge and the Action Centre list so the two
 * read the same list and a "mark as seen" in the list moves the badge at once.
 *
 * Extracted from ActionCentreModal (ACEA-01). The match on reporter name and
 * the per-browser "seen" record are carried over unchanged — both are named
 * gaps for a later phase (reporter is stored as a display name, not a user id;
 * "seen" is not shared across devices).
 */

import { useEffect, useSyncExternalStore } from 'react';
import { listBugReportsForUser } from '../api/bugReportApi.js';

const EMPTY = [];
const stores = new Map();

function ackKeyFor(tenantId, username) {
  return `seasuite_bug_acks_${tenantId}_${username}`;
}

function readAcks(key) {
  try {
    return JSON.parse(localStorage.getItem(key) || '{}') || {};
  } catch {
    return {};
  }
}

function getStore(tenantId, session) {
  const key = `${tenantId}::${session.username}`;
  let store = stores.get(key);
  if (!store) {
    store = {
      key,
      bugs: EMPTY,
      listeners: new Set(),
      loaded: false,
      failed: false,
      inflight: null,
    };
    stores.set(key, store);
  }
  return store;
}

function emit(store) {
  for (const l of store.listeners) l();
}

function refresh(store, tenantId, session) {
  if (store.inflight) return store.inflight;
  const reporterName = session.displayName || session.username;
  const ackKey = ackKeyFor(tenantId, session.username);
  store.inflight = listBugReportsForUser()
    .then((bugs) => {
      const acks = readAcks(ackKey);
      store.bugs = (bugs || []).filter(
        (b) =>
          b.reporter_username === reporterName && b.status !== 'reported' && acks[b.id] !== b.status
      );
      store.loaded = true;
      store.failed = false;
      emit(store);
    })
    .catch(() => {
      // GH #8721: a failed read is unknown, never "no updates". The last good
      // list is kept; `failed` lets the badge say its count may be low.
      store.failed = true;
      emit(store);
    })
    .finally(() => {
      store.inflight = null;
    });
  return store.inflight;
}

/**
 * @param {object} session  the viewer's session
 * @param {object} tenant   the current tenant; nothing is fetched without one
 * @param {object} [opts]   { refreshOnMount } — re-fetch when the caller mounts
 * @returns {{ bugUpdates: Array, failed: boolean, acknowledgeBug: (bug) => void }}
 */
export function useMyBugUpdates(session, tenant, { refreshOnMount = false } = {}) {
  const tenantId = tenant?.id;
  const enabled = Boolean(session?.username && tenantId);
  const store = enabled ? getStore(tenantId, session) : null;

  const bugUpdates = useSyncExternalStore(
    (cb) => {
      if (!store) return () => {};
      store.listeners.add(cb);
      return () => store.listeners.delete(cb);
    },
    () => (store ? store.bugs : EMPTY)
  );
  const failed = useSyncExternalStore(
    (cb) => {
      if (!store) return () => {};
      store.listeners.add(cb);
      return () => store.listeners.delete(cb);
    },
    () => Boolean(store && store.failed)
  );

  useEffect(() => {
    if (!store) return;
    if (refreshOnMount || !store.loaded) refresh(store, tenantId, session);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store, refreshOnMount]);

  const acknowledgeBug = (bug) => {
    if (!store) return;
    const ackKey = ackKeyFor(tenantId, session.username);
    const acks = readAcks(ackKey);
    acks[bug.id] = bug.status;
    try {
      localStorage.setItem(ackKey, JSON.stringify(acks));
    } catch {
      /* storage unavailable — the item clears for this session only */
    }
    store.bugs = store.bugs.filter((b) => b.id !== bug.id);
    emit(store);
  };

  return { bugUpdates, failed, acknowledgeBug };
}
